import { useQuery, useMutation, useQueryClient } from 'react-query'
import { queueAPI } from '../api/client'
import { useToast } from '../components/Toast'
import {
  Play,
  Pause,
  RefreshCw,
  Trash2,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader
} from 'lucide-react'

function TaskRow({ task, onRetry, onDelete }) {
  const statusIcons = {
    pending: <Clock className="text-yellow-400" size={18} />,
    processing: <Loader className="text-blue-400 animate-spin" size={18} />,
    completed: <CheckCircle className="text-green-400" size={18} />,
    failed: <AlertCircle className="text-red-400" size={18} />,
  }

  return (
    <tr>
      <td className="font-mono text-sm">#{task.id}</td>
      <td>
        <span className="badge badge-primary">{task.task_type}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          {statusIcons[task.status]}
          <span>{task.status}</span>
        </div>
      </td>
      <td className="text-sm opacity-60">
        {task.attempts}/{task.max_attempts}
      </td>
      <td className="text-sm opacity-60">
        {new Date(task.created_at).toLocaleString()}
      </td>
      <td>
        <div className="flex gap-1">
          {task.status === 'failed' && (
            <button
              onClick={() => onRetry(task.id)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Повторить"
            >
              <RefreshCw size={16} />
            </button>
          )}
          {task.status !== 'processing' && (
            <button
              onClick={() => onDelete(task.id)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors text-red-400"
              title="Удалить"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

export default function Queue() {
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  const { data: status } = useQuery('queueStatus', queueAPI.getStatus, {
    refetchInterval: 3000
  })

  const { data: stats } = useQuery('queueStats', queueAPI.getStats)

  const { data: tasks, isLoading } = useQuery('tasks', () => queueAPI.getTasks({ limit: 50 }), {
    refetchInterval: 5000
  })

  const retryMutation = useMutation(queueAPI.retryTask, {
    onSuccess: () => {
      queryClient.invalidateQueries('tasks')
      addToast('Задача поставлена на повтор')
    },
    onError: (err) => {
      addToast(err.response?.data?.detail || 'Не удалось повторить задачу', 'error')
    }
  })

  const deleteMutation = useMutation(queueAPI.deleteTask, {
    onSuccess: () => {
      queryClient.invalidateQueries('tasks')
      queryClient.invalidateQueries('queueStats')
      addToast('Задача удалена')
    },
    onError: (err) => {
      addToast(err.response?.data?.detail || 'Не удалось удалить задачу', 'error')
    }
  })

  const clearMutation = useMutation(() => queueAPI.clearCompleted(24), {
    onSuccess: (data) => {
      queryClient.invalidateQueries('tasks')
      queryClient.invalidateQueries('queueStats')
      addToast(`Очищено завершённых задач: ${data.deleted || 0}`)
    },
    onError: (err) => {
      addToast(err.response?.data?.detail || 'Не удалось очистить задачи', 'error')
    }
  })

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Очередь задач</h1>
        <div className="flex gap-2">
          <button
            onClick={() => clearMutation.mutate()}
            className="btn-ghost"
            disabled={clearMutation.isLoading}
          >
            Очистить завершённые
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="glass p-4">
          <div className="flex items-center gap-2 mb-2">
            {status?.is_running ? (
              <Play className="text-green-400" size={20} />
            ) : (
              <Pause className="text-yellow-400" size={20} />
            )}
            <span className="font-semibold">Воркер</span>
          </div>
          <p className={status?.is_running ? 'text-green-400' : 'text-yellow-400'}>
            {status?.is_running ? 'Работает' : 'Остановлен'}
          </p>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="text-yellow-400" size={20} />
            <span className="font-semibold">Ожидают</span>
          </div>
          <p className="text-2xl font-bold">{stats?.pending || 0}</p>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="text-green-400" size={20} />
            <span className="font-semibold">Завершены</span>
          </div>
          <p className="text-2xl font-bold">{stats?.completed || 0}</p>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="text-red-400" size={20} />
            <span className="font-semibold">Ошибки</span>
          </div>
          <p className="text-2xl font-bold">{stats?.failed || 0}</p>
        </div>
      </div>

      {/* Current task */}
      {status?.current_task && (
        <div className="glass p-4 mb-6">
          <h3 className="font-semibold mb-2">Сейчас обрабатывается</h3>
          <div className="flex items-center gap-4">
            <Loader className="text-blue-400 animate-spin" size={24} />
            <div>
              <p>Задача #{status.current_task.id} - {status.current_task.task_type}</p>
              <p className="text-sm opacity-50">
                Попытка {status.current_task.attempts} из {status.current_task.max_attempts}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tasks table */}
      <div className="glass overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 opacity-50">Загрузка...</div>
        ) : tasks?.length === 0 ? (
          <div className="text-center py-12 opacity-50">В очереди задач нет</div>
        ) : (
          <table className="table-dark">
            <thead>
              <tr>
                <th>ID</th>
                <th>Тип</th>
                <th>Статус</th>
                <th>Попытки</th>
                <th>Создано</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {tasks?.map(task => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onRetry={(id) => retryMutation.mutate(id)}
                  onDelete={(id) => deleteMutation.mutate(id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
